import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FileTextOutlined,
  TeamOutlined,
  ShoppingOutlined,
  BulbOutlined,
  CalendarOutlined,
  FunnelPlotOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { usePermissions, SCOPES, ACTIONS } from '../../contexts/PermissionsContext';
import './MobileQuickActions.css';

interface QuickAction {
  key: string;
  icon: React.ReactNode;
  label: string;
  gradient: string;
  path: string;
}

const HIDDEN_PATHS = ['/quotes/new', '/clients/new', '/products/new'];
const EDIT_PATTERN = /\/(quotes|clients|products)\/\d+\/edit$/;

const MobileQuickActions: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const path = location.pathname;

  const actions: QuickAction[] = useMemo(() => {
    // Don't show on create/edit pages
    if (HIDDEN_PATHS.includes(path) || EDIT_PATTERN.test(path)) return [];
    if (path === '/quotes' || (path.startsWith('/quotes/') && !path.startsWith('/quotes/new'))) {
      if (!hasPermission(SCOPES.QUOTES, ACTIONS.CREATE)) return [];
      return [{
        key: 'new-quote',
        icon: <FileTextOutlined />,
        label: 'Nuovo Preventivo',
        gradient: 'linear-gradient(135deg, #EB2ADD 0%, #0D12F2 100%)',
        path: '/quotes/new',
      }];
    }

    if (path === '/clients' || path.startsWith('/clients/')) {
      if (!hasPermission(SCOPES.CLIENTS, ACTIONS.CREATE)) return [];
      return [{
        key: 'new-client',
        icon: <TeamOutlined />,
        label: 'Nuovo Cliente',
        gradient: 'linear-gradient(135deg, #010D69 0%, #0D12F2 100%)',
        path: '/clients?action=new',
      }];
    }

    if (path === '/products' || path.startsWith('/products/')) {
      if (!hasPermission(SCOPES.PRODUCTS, ACTIONS.CREATE)) return [];
      return [{
        key: 'new-product',
        icon: <ShoppingOutlined />,
        label: 'Nuovo Prodotto',
        gradient: 'linear-gradient(135deg, #0D12F2 0%, #06b6d4 100%)',
        path: '/products?action=new',
      }];
    }

    if (path === '/option-groups' || path.startsWith('/option-groups/')) {
      if (!hasPermission(SCOPES.PRODUCTS, ACTIONS.CREATE)) return [];
      return [{
        key: 'new-option-group',
        icon: <BulbOutlined />,
        label: 'Nuovo Gruppo Opzioni',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
        path: '/option-groups?action=new',
      }];
    }

    if (path === '/appointments' || path.startsWith('/appointments/')) {
      return [{
        key: 'new-appointment',
        icon: <CalendarOutlined />,
        label: 'Nuovo Appuntamento',
        gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        path: '/appointments?action=new',
      }];
    }

    if (path === '/opportunities' || path.startsWith('/opportunities/')) {
      return [{
        key: 'new-opportunity',
        icon: <FunnelPlotOutlined />,
        label: 'Nuova Opportunità',
        gradient: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
        path: '/opportunities?action=new',
      }];
    }

    return [];
  }, [path, hasPermission]);

  if (actions.length === 0) return null;

  const handleAction = (actionPath: string) => {
    setIsOpen(false);
    navigate(actionPath);
  };

  return (
    <>
      {/* Linguetta: small handle tab at the bottom edge of the header */}
      <button
        className={`mqa-tab ${isOpen ? 'mqa-tab--open' : ''}`}
        onClick={() => setIsOpen(v => !v)}
        aria-label="Azioni rapide"
        aria-expanded={isOpen}
      >
        <span className="mqa-tab__bar" />
        <DownOutlined className="mqa-tab__chevron" />
      </button>

      {/* Backdrop overlay */}
      {isOpen && (
        <div
          className="mqa-overlay"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Slide-down action panel */}
      <div
        className={`mqa-panel ${isOpen ? 'mqa-panel--open' : ''}`}
        aria-hidden={!isOpen}
      >
        <div className="mqa-panel__content">
          {actions.map(action => (
            <button
              key={action.key}
              className="mqa-action"
              onClick={() => handleAction(action.path)}
            >
              <span
                className="mqa-action__icon"
                style={{ background: action.gradient }}
              >
                {action.icon}
              </span>
              <span className="mqa-action__label">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
    </>
  );
};

export default MobileQuickActions;
